import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiService from "../../../services/api";
import useCodeCommon from "../../../hooks/useCodeCommon";
import useHubManagers from "../../../hooks/useHubManagers";
import useHubs from "../../../hooks/useHubs";
import useRoleBasedHubFiltering from "../../../hooks/useRoleBasedHubFiltering";
import {
  FormSection,
  TextInput,
  SelectInput,
  SearchableSelectInput,
  MobileInput,
  FileUploadInput,
} from "../../../components/forms";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import CustomLoading from "../../../components/common/CustomLoading";

interface VehicleModelCode {
  codeid: number;
  codevalue: string;
  codedisplayname: string;
  categoryid: number;
  codediscription: string;
  categoryname: string;
  displayorder: number;
}


// Common image base URL and default (from environment)
const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
const DEFAULT_IMAGE = "default.png";

const VEHICLE_STATUS_UI_ALLOWLIST = new Set(
  ["Active", "Under Maintenance", "B2B", "Inactive"].map((s) => s.toLowerCase())
);



const EditVehiclePage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    // Basic Information
    vehiclename: "",
    model: "",
    speedtype: "",
    range: "",
    topspeed: "",
    weight: "",
    mileagekm: "",
    batterycapacitykwh: "",
    rangeperchargekm: "",
    motorpower: "",
    chargingtime: "",
    color: "",

    // Registration & Connection
    registrationnumber: "",
    telematicsdeviceid: "",
    hubmasterid: "",
    assignedHub: "", // For display purposes
    status: "",
    vehicletype: "",
    vinnumber: "",
    vehiclenum: "",
    motorid: "",

    // Battery Information
    batteryno1: "",
    batteryno2: "",
    chargeno: "",

    // Specification
    otherspecifications: "",

    // Uploads - now support multiple images
    vehicleimages: [] as string[],
    batteryimages: [] as string[],
    chargerimages: [] as string[],

    id: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [imageModal, setImageModal] = useState<{ open: boolean; src: string; title: string }>({ open: false, src: "", title: "" });
  const [vehicleModelCodes, setVehicleModelCodes] = useState<VehicleModelCode[]>([]);
  const [vehicleModelLoading, setVehicleModelLoading] = useState(false);
  const [vehicleModelError, setVehicleModelError] = useState<string | null>(null);
  const [originalStatusCode, setOriginalStatusCode] = useState<number | null>(null);
  const hasFetched = useRef(false);

  const { options: allVehicleStatusOptions, loading: vehicleStatusLoading } = useCodeCommon("VehicleStatus");

  const vehicleStatusOptions = allVehicleStatusOptions.filter((option) =>
    VEHICLE_STATUS_UI_ALLOWLIST.has(String(option.codedisplayname || "").trim().toLowerCase())
  );
  const { options: vehicleTypeOptions, loading: vehicleTypeLoading } = useCodeCommon("VehicleType");
  const { options: evSpeedTypeOptions, loading: evSpeedTypeLoading } = useCodeCommon("EVSpeedType");
  const { hubManagers, loading: hubManagersLoading, error: hubManagersError } = useHubManagers();
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { filterHubOptions } = useRoleBasedHubFiltering();

  // Get vehicle model options from codes
  const vehicleModelOptions = vehicleModelCodes.map(code => code.codedisplayname);

  const getVehicleModelCodeValue = (displayName: string): string | null => {
    const code = vehicleModelCodes.find(c => c.codedisplayname === displayName);
    return code ? code.codevalue : null;
  };

  const getVehicleModelDisplayName = (codeValue: string | number | null | undefined): string => {
    if (codeValue === null || codeValue === undefined) return '';
    const code = vehicleModelCodes.find(c => c.codevalue === String(codeValue));
    return code?.codedisplayname || '';
  };

  // Filter hub options based on user role
  const filteredHubOptions = filterHubOptions(hubOptions);

  // Build preview src from value that can be a URL, path, or JSON array string
  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
    } catch {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
  };

  // Parse vehicleimages JSON array and extract individual images
  const parseVehicleImages = (raw: any): string[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as string[];
    let value: any = raw;
    for (let i = 0; i < 3; i += 1) {
      if (typeof value === "string") {
        try {
          value = JSON.parse(value);
        } catch {
          break;
        }
      }
    }
    if (Array.isArray(value)) {
      const flattened = value.flat?.(3) ?? value;
      return flattened.filter((x: any) => typeof x === "string");
    }
    return [];
  };

  // Normalize image value to a clean path like "/Miscellaneous/abc.png"
  const normalizeImagePath = (value: string): string => {
    try {
      const trimmed = String(value || "").trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("[")) {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr) && arr.length > 0) {
          return String(arr[0] || "");
        }
      }
      const lower = trimmed.toLowerCase();
      const marker = "/commonservice/images/";
      const idx = lower.indexOf(marker);
      if (idx !== -1) {
        const after = trimmed.substring(idx + marker.length);
        return after.startsWith("/") ? after : `/${after}`;
      }
      return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    } catch {
      return String(value || "");
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    // Clear field error on valid input
    setErrors((prev) => {
      const next = { ...prev };
      const str = Array.isArray(value) ? value.join("") : String(value ?? "");
      if (str.trim() !== "") delete (next as any)[field];
      // If vehicletype toggles to Without Battery, clear battery-related errors
      if (field === "vehicletype" && value === "Without Battery") {
        delete next.batteryno1;
        delete next.batteryno2;
        delete next.chargeno;
        delete next.batteryimages;
        delete next.chargerimages;
      }
      if (field === "speedtype") {
        const speedTypeStr = String(value ?? "").toLowerCase();
        if (speedTypeStr.includes("low")) {
          delete next.vinnumber;
          delete next.registrationnumber;
        }
      }
      return next;
    });

    if (field === "speedtype") {
      const speedTypeStr = String(value ?? "").toLowerCase();
      if (speedTypeStr.includes("low")) {
        setFormData((prev) => ({ ...prev, vinnumber: "", registrationnumber: "", batteryno2: "" }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next.batteryno2;
          return next;
        });
      }
    }

    if (field === "assignedHub") {
      // Find the hubmasterid for the selected hub name
      const selectedHub = hubOptions.find(hub => hub.hubname === value);
      setFormData((prev) => ({ 
        ...prev, 
        assignedHub: value as string,
        hubmasterid: selectedHub ? selectedHub.hubmasterid.toString() : ""
      }));
    } else if (field === "vehicleimages" || field === "batteryimages" || field === "chargerimages") {
      // Handle image array fields - append new images to existing array
      const newImages = Array.isArray(value) ? value : (value ? [value] : []);
      setFormData((prev) => {
        const existingImages = Array.isArray(prev[field as keyof typeof prev]) ? prev[field as keyof typeof prev] as string[] : [];
        return { ...prev, [field]: [...existingImages, ...newImages] };
      });
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handlePreview = (title: string, value: string) => {
    setImageModal({ open: true, src: resolvePreviewSrc(value), title });
  };

  const handleDownload = async (value: string) => {
    try {
      const url = resolvePreviewSrc(value);
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const rawName = (value || 'image').toString().split('/').pop() || 'image';
      const extFromType = blob.type && blob.type.includes('/') ? `.${blob.type.split('/')[1]}` : '';
      link.download = rawName.includes('.') ? rawName : `${rawName}${extFromType || '.png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // Fallback: open in new tab if direct download fails
      const fallbackUrl = resolvePreviewSrc(value);
      window.open(fallbackUrl, '_blank');
    }
  };

  const handleDeleteImage = (field: 'vehicleimages' | 'batteryimages' | 'chargerimages') => {
    setFormData((prev) => ({ ...prev, [field]: [] }));
    setErrors((prev) => {
      const next = { ...prev };
      delete (next as any)[field];
      return next;
    });
  };

  const handleDeleteImageFromArray = (field: 'vehicleimages' | 'batteryimages' | 'chargerimages', index: number) => {
    setFormData((prev) => {
      const currentArray = (prev[field] as string[]) || [];
      const newArray = currentArray.filter((_, i) => i !== index);
      return { ...prev, [field]: newArray };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete (next as any)[field];
      return next;
    });
  };

  // Fetch vehicle models on component mount
  useEffect(() => {
    const fetchVehicleModels = async () => {
      try {
        setVehicleModelLoading(true);
        setVehicleModelError(null);
        const response: any = await apiService.getAllCodeCommonByCategory("VehicleModel");
        const raw = (response as any)?.data || response || [];
        const codes = Array.isArray(raw) ? raw : [];
        // Sort by displayorder
        const sortedCodes = [...codes].sort((a: VehicleModelCode, b: VehicleModelCode) => 
          a.displayorder - b.displayorder
        );
        setVehicleModelCodes(sortedCodes);
      } catch (err: any) {
        console.error('Error fetching VehicleModel codes:', err);
        setVehicleModelError(err.message || 'Failed to fetch vehicle model options');
        setVehicleModelCodes([]);
      } finally {
        setVehicleModelLoading(false);
      }
    };

    fetchVehicleModels();
  }, []);

  const fetchVehicleById = async (vehicleId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getVehicleById(vehicleId);
      const data = (response as any)?.data || response || {};

      // Get model display name from vehiclemodelid
      const modelDisplayName = getVehicleModelDisplayName(data.vehiclemodelid || data.vehiclemodel);

      const mapped = {
        // Basic Information
        vehiclename: data.vehiclename || data.vehicleName || "",
        model: modelDisplayName || data.vehiclemodel || data.model || "",
        speedtype: (evSpeedTypeOptions.find(s => s.codevalue === String(data.speedtype))?.codedisplayname ?? ""),
        range: (data.rangeperchargekm ?? data.range ?? "").toString(),
        topspeed: (data.topspeed ?? data.topspeedkmph ?? data.topSpeed ?? "").toString(),
        weight: (data.weight ?? "").toString(),
        mileagekm: (data.mileagekm ?? data.mileage ?? "").toString(),
        batterycapacitykwh: (data.batterycapacitykwh ?? data.batteryCapacity ?? "").toString(),
        rangeperchargekm: (data.rangeperchargekm ?? data.range ?? "").toString(),
        motorpower: data.motorpower || data.motorPower || "",
        chargingtime: data.chargingtime || data.chargingTime || "",
        color: data.color || "",

        // Registration & Connection
        registrationnumber: data.registrationnumber || data.registrationNumber || "",
        telematicsdeviceid: data.telematicsdeviceid || data.telematicService || "",
        hubmasterid: (data.hubmasterid ?? data.hubId ?? "").toString(),
        assignedHub: (() => {
          const hubId = data.hubmasterid ?? data.hubId;
          if (hubId && hubOptions && hubOptions.length > 0) {
            const hub = hubOptions.find(h => h.hubmasterid === hubId);
            return hub ? hub.hubname : "";
          }
          return "";
        })(),
        status: (allVehicleStatusOptions.find(s => s.codevalue === String(data.status))?.codedisplayname ?? ""),
        vehicletype: (vehicleTypeOptions.find(t => t.codevalue === String(data.vehicletype))?.codedisplayname ?? ""),
        vinnumber: data.vinnumber || data.vinNumber || "",
        vehiclenum: data.vehiclenum || "",
        motorid: data.motorid || "",

        // Battery Information
        batteryno1: data.batteryno1 || data.batteryNo1 || "",
        batteryno2: data.batteryno2 || data.batteryNo2 || "",
        chargeno: data.chargeno || data.chargeNo || "",

        // Specification
        otherspecifications: data.otherspecifications || data.otherSpecifications || "",

        // Uploads - parse vehicleimages array and categorize by folder path
        vehicleimages: (() => {
          const images = parseVehicleImages(data.vehicleimages);
          const vehicleImgs = images.filter(img => img.includes('/Vehicle_Images/') || img.includes('/vehicle/')) || [];
          return vehicleImgs;
        })(),
        batteryimages: (() => {
          const images = parseVehicleImages(data.vehicleimages);
          const batteryImgs = images.filter(img => img.includes('/Battery_Images/') || img.includes('/battery/')) || [];
          return batteryImgs;
        })(),
        chargerimages: (() => {
          const images = parseVehicleImages(data.vehicleimages);
          const chargerImgs = images.filter(img => img.includes('/Charger_Images/') || img.includes('/charger/')) || [];
          return chargerImgs;
        })(),

        id: (data.vehicleid ?? data.id ?? vehicleId).toString(),
      };
      
      setFormData(mapped);
      // Store original status code to check if vehicle is booked
      setOriginalStatusCode(data.status);
    } catch (err: any) {
      setError(err.message || 'Failed to load vehicle');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      id &&
      !hasFetched.current &&
      vehicleStatusOptions.length > 0 &&
      vehicleTypeOptions.length > 0 &&
      evSpeedTypeOptions.length > 0 &&
      hubOptions.length > 0 &&
      vehicleModelCodes.length > 0
    ) {
      hasFetched.current = true;
      fetchVehicleById(id);
    }
  }, [id, vehicleStatusOptions.length, vehicleTypeOptions.length, evSpeedTypeOptions.length, hubOptions.length, vehicleModelCodes.length]);

  const handleUpdate = async () => {
    setLoading(true);
    setError(null);
    try {
      // Validate required fields (all except otherspecifications)
      const nextErrors: Record<string, string> = {};
      const isWithoutBattery = formData.vehicletype === "Without Battery";

      const requireField = (key: string, label: string) => {
        const value = (formData as any)[key];
        if (value === undefined || value === null || String(value).trim() === "") {
          nextErrors[key] = `${label} is required`;
        }
      };

      // Basic Information
      requireField("vehiclename", "Vehicle Name");
      requireField("model", "Model");
      requireField("speedtype", "Speed Type");
      requireField("range", "Range");
      requireField("topspeed", "Top Speed");
      requireField("weight", "Weight");

      // Registration & Connection
      const isHighSpeed = formData.speedtype && formData.speedtype.toLowerCase().includes("high");
      if (isHighSpeed) {
        requireField("registrationnumber", "Registration No.");
        requireField("vinnumber", "VIN Number");
      }
      requireField("telematicsdeviceid", "Telematics Device ID");
      requireField("vehiclenum", "Vehicle Number");
      requireField("assignedHub", "Assign Hub");
      if (originalStatusCode !== 4) {
        requireField("status", "Status");
      }
      if (!isHighSpeed) {
        requireField("motorid", "Motor ID");
      }

      // Uploads - check if at least one image is uploaded
      if (!formData.vehicleimages || formData.vehicleimages.length === 0) {
        nextErrors.vehicleimages = "At least one vehicle image is required";
      }
      if (!isWithoutBattery) {
        // Battery Information
        requireField("batteryno1", "Battery No 1");
        if (isHighSpeed) {
          requireField("batteryno2", "Battery No 2");
        }
        requireField("chargeno", "Charge No");
        // Battery uploads - check if at least one image is uploaded
        if (!formData.batteryimages || formData.batteryimages.length === 0) {
          nextErrors.batteryimages = "At least one battery image is required";
        }
        if (!formData.chargerimages || formData.chargerimages.length === 0) {
          nextErrors.chargerimages = "At least one charger image is required";
        }
      }

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        throw new Error("Please fill all required fields");
      }

      const apiData = {
        vehicleid: parseInt(formData.id) || 0,
        vehiclename: formData.vehiclename,
        vehiclemodel: (() => {
          const modelCodeValue = getVehicleModelCodeValue(formData.model);
          return modelCodeValue ? parseInt(modelCodeValue) : 0;
        })(),
        topspeed: parseFloat(formData.topspeed),
        weight: formData.weight ? parseFloat(formData.weight) : 0,
        speedtype: (() => {
          const speedTypeOption = evSpeedTypeOptions.find(s => s.codedisplayname === formData.speedtype);
          return speedTypeOption ? parseInt(speedTypeOption.codevalue) : 1;
        })(),
        batterycapacitykwh: 0,
        rangeperchargekm: parseFloat(formData.range),
        motorpower: "",
        chargingtime: "",
        color: "",
        registrationnumber: formData.registrationnumber,
        telematicsdeviceid: formData.telematicsdeviceid,
        hubmasterid: parseInt(formData.hubmasterid),
        status: (() => {
          if (originalStatusCode === 4) {
            return originalStatusCode;
          }
          const statusOption = allVehicleStatusOptions.find(s => s.codedisplayname === formData.status);
          if (statusOption) {
            return parseInt(statusOption.codevalue);
          }
          return originalStatusCode;
        })(),
        userid: 0,
        vehicleimages: (() => {
          // Combine all image arrays into one
          const allImages = [
            ...(formData.vehicleimages || []),
            ...(formData.batteryimages || []),
            ...(formData.chargerimages || [])
          ];
          const paths = allImages
            .map(v => normalizeImagePath(v as string))
            .filter(p => Boolean(p));
          return paths as string[];
        })(),
        vehicletype: (() => {
          const vehicleTypeOption = vehicleTypeOptions.find(t => t.codedisplayname === formData.vehicletype);
          return vehicleTypeOption ? parseInt(vehicleTypeOption.codevalue) : 1;
        })(),
        vinnumber: formData.vinnumber,
        vehiclenum: formData.vehiclenum,
        motorid: formData.motorid,
        batteryno1: formData.batteryno1,
        batteryno2: formData.batteryno2,
        chargeno: formData.chargeno,
        otherspecifications: formData.otherspecifications,
      };

      await apiService.updateVehicle(formData.id, apiData);
      navigate('/vehicle-management', { state: location.state });
    } catch (err: any) {
      setError(err.message || 'Failed to update vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (loading || vehicleModelLoading) {
    return <CustomLoading message="Fetching Vehicle Details..." />;
  }

  return (
    <Container fluid>
      {(error || vehicleModelError) && (
        <Alert severity="error" className="mb-3" onClose={() => setError(null)}>
          {error || vehicleModelError}
        </Alert>
      )}

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Basic Information
        </Typography>
        <Row className="g-3">
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="vehiclename"
              formData={formData}
              onChange={handleInputChange}
              label="Vehicle Name"
              required
              error={errors.vehiclename}
              placeholder="Enter vehicle name"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SearchableSelectInput
              field="model"
              formData={formData}
              onChange={handleInputChange}
              label="Model"
              required
              error={errors.model}
              disabled={vehicleModelLoading}
              loading={vehicleModelLoading}
              placeholder="Search and select model"
              options={vehicleModelOptions || []}
            />
          </Col>
            <Col xs={12} md={6} lg={3}>
              <SelectInput
                field="speedtype"
                formData={formData}
                onChange={handleInputChange}
                label="Speed Type"
                required
                error={errors.speedtype}
                disabled={evSpeedTypeLoading}
                options={evSpeedTypeLoading ? ["Loading..."] : (evSpeedTypeOptions && evSpeedTypeOptions.length > 0 ? evSpeedTypeOptions.map(speedType => speedType.codedisplayname) : ["No speed type available"])}
              />
            </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="range"
              formData={formData}
              onChange={handleInputChange}
              label="Range (km)"
              required
              error={errors.range}
              placeholder="Enter range"
              type="number"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="topspeed"
              formData={formData}
              onChange={handleInputChange}
              label="Top Speed (kmph)"
              required
              error={errors.topspeed}
              placeholder="Enter top speed"
              type="number"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="weight"
              formData={formData}
              onChange={handleInputChange}
              label="Weight"
              required
              error={errors.weight}
              placeholder="Enter weight"
              type="number"
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Registration & Connection
        </Typography>
        <Row className="g-3">
          {formData.speedtype && formData.speedtype.toLowerCase().includes("high") && (
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="registrationnumber"
                formData={formData}
                onChange={handleInputChange}
                label="Registration No."
                required
                error={errors.registrationnumber}
                placeholder="Enter registration no."
              />
            </Col>
          )}
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="vehiclenum"
              formData={formData}
              onChange={handleInputChange}
              label="Vehicle Number"
              required
              error={errors.vehiclenum}
              placeholder="Enter vehicle number"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="telematicsdeviceid"
              formData={formData}
              onChange={handleInputChange}
              label="Telematics Device ID"
            required
            error={errors.telematicsdeviceid}
              placeholder="Enter telematics device ID"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SearchableSelectInput
              field="assignedHub"
              formData={formData}
              onChange={handleInputChange}
              label="Assign Hub"
              required
            error={errors.assignedHub}
              disabled={hubsLoading}
              loading={hubsLoading}
              placeholder="Search and select hub"
              options={
                hubsLoading
                  ? []
                  : filteredHubOptions.map((hub: any) => hub.hubname)
              }
            />
          </Col>
          {originalStatusCode !== 4 && (
            <Col xs={12} md={6} lg={3}>
              <SelectInput
                field="status"
                formData={formData}
                onChange={handleInputChange}
                label="Status"
                required
                error={errors.status}
                disabled={vehicleStatusLoading}
                options={vehicleStatusLoading ? ["Loading..."] : (vehicleStatusOptions && vehicleStatusOptions.length > 0 ? vehicleStatusOptions.map(status => status.codedisplayname) : ["No status available"])}
              />
            </Col>
          )}
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="vehicletype"
              formData={formData}
              onChange={handleInputChange}
              label="Vehicle Type"
            required
            error={errors.vehicletype}
              disabled={vehicleTypeLoading}
              options={vehicleTypeLoading ? ["Loading..."] : (vehicleTypeOptions && vehicleTypeOptions.length > 0 ? vehicleTypeOptions.map(vehicleType => vehicleType.codedisplayname) : ["No vehicle type available"])}
            />
          </Col>
          {formData.speedtype && formData.speedtype.toLowerCase().includes("high") && (
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="vinnumber"
                formData={formData}
                onChange={handleInputChange}
                label="VIN Number"
                required
                error={errors.vinnumber}
                placeholder="Enter VIN number"
              />
            </Col>
          )}
          
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="motorid"
              formData={formData}
              onChange={handleInputChange}
              label="Motor ID"
              required={Boolean(formData.speedtype && formData.speedtype.toLowerCase().includes("low"))}
              error={errors.motorid}
              placeholder="Enter Motor ID"
            />
          </Col>
        </Row>
      </FormSection>

      {formData.vehicletype !== "Without Battery" && (
        <FormSection>
          <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
            Battery Information
          </Typography>
          <Row className="g-3">
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="batteryno1"
                formData={formData}
                onChange={handleInputChange}
                label="Battery No 1."
                required
                error={errors.batteryno1}
                placeholder="Enter battery no."
              />
            </Col>
            {formData.speedtype && formData.speedtype.toLowerCase().includes("high") && (
              <Col xs={12} md={6} lg={3}>
                <TextInput
                  field="batteryno2"
                  formData={formData}
                  onChange={handleInputChange}
                  label="Battery No 2."
                  required
                  error={errors.batteryno2}
                  placeholder="Enter battery no."
                />
              </Col>
            )}
            <Col xs={12} md={6} lg={3}>
              <TextInput
                field="chargeno"
                formData={formData}
                onChange={handleInputChange}
                label="Charger No."
                required
                error={errors.chargeno}
                placeholder="Enter charger no."
              />
            </Col>
          </Row>
        </FormSection>
      )}

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Specification
        </Typography>
        <Row className="g-3">
          <Col xs={12}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Other Information"
              placeholder="Enter other information"
              value={formData.otherspecifications}
              onChange={(e) => handleInputChange("otherspecifications", e.target.value)}
              multiline
              rows={4}
              sx={{
                "& .MuiFormLabel-asterisk": {
                  color: "red",
                },
              }}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection>
        <Typography variant="subtitle2" className="mb-2" sx={{ color: 'rgba(22, 67, 135, 1)', fontWeight: 600 }}>
          Attachments
        </Typography>
        <Row className="g-3">
          <Col xs={12} md={4}>
            <FileUploadInput
              field="vehicleimages"
              formData={formData}
              onChange={handleInputChange}
              label="Upload Vehicle Images"
              folderPath="vehicle"
              folderType="vehicle"
              required
              multiple
              maxFiles={5}
              error={errors.vehicleimages}
            />
          </Col>
          {formData.vehicletype !== "Without Battery" && (
            <>
              <Col xs={12} md={4}>
                <FileUploadInput
                  field="batteryimages"
                  formData={formData}
                  onChange={handleInputChange}
                  label="Upload Battery Images"
                  folderPath="battery"
                  folderType="battery"
                  required
                  multiple
                  maxFiles={2}
                  error={errors.batteryimages}
                />
              </Col>
              <Col xs={12} md={4}>
                <FileUploadInput
                  field="chargerimages"
                  formData={formData}
                  onChange={handleInputChange}
                  label="Upload Charger Images"
                  folderPath="charger"
                  folderType="charger"
                  required
                  multiple
                  maxFiles={2}
                  error={errors.chargerimages}
                />
              </Col>
            </>
          )}
        </Row>
        
        {/* Image Preview Section */}
        {((Array.isArray(formData.vehicleimages) && formData.vehicleimages.length > 0) || 
          (Array.isArray(formData.batteryimages) && formData.batteryimages.length > 0) || 
          (Array.isArray(formData.chargerimages) && formData.chargerimages.length > 0)) && (
          <Row className="g-3 mt-3">
            <Col xs={12}>
              <Typography variant="body2" color="textSecondary" className="mb-2">
                Uploaded Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Vehicle Images */}
                {Array.isArray(formData.vehicleimages) && formData.vehicleimages.map((image, index) => (
                  <ImagePreviewCard
                    key={`vehicle-${index}`}
                    title={`Vehicle Image ${index + 1}`}
                    value={image}
                    resolveSrc={resolvePreviewSrc}
                    onDelete={() => handleDeleteImageFromArray('vehicleimages', index)}
                    size={200}
                    defaultImage={DefaultImage}
                  />
                ))}
                
                {/* Battery Images */}
                {formData.vehicletype !== "Without Battery" && Array.isArray(formData.batteryimages) && formData.batteryimages.map((image, index) => (
                  <ImagePreviewCard
                    key={`battery-${index}`}
                    title={`Battery Image ${index + 1}`}
                    value={image}
                    resolveSrc={resolvePreviewSrc}
                    onDelete={() => handleDeleteImageFromArray('batteryimages', index)}
                    size={200}
                    defaultImage={DefaultImage}
                  />
                ))}
                
                {/* Charger Images */}
                {formData.vehicletype !== "Without Battery" && Array.isArray(formData.chargerimages) && formData.chargerimages.map((image, index) => (
                  <ImagePreviewCard
                    key={`charger-${index}`}
                    title={`Charger Image ${index + 1}`}
                    value={image}
                    resolveSrc={resolvePreviewSrc}
                    onDelete={() => handleDeleteImageFromArray('chargerimages', index)}
                    size={200}
                    defaultImage={DefaultImage}
                  />
                ))}
              </Box>
            </Col>
          </Row>
        )}
      </FormSection>

      {/* Image Modal */}
      {imageModal.open && (
        <Box
          onClick={() => setImageModal({ open: false, src: '', title: '' })}
          sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
        >
          <Box sx={{ backgroundColor: '#000', borderRadius: 1, maxWidth: '90vw', maxHeight: '90vh', p: 1 }}>
            <img src={imageModal.src} alt={imageModal.title} style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />
          </Box>
        </Box>
      )}

      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          className="px-2 py-2 fw-medium text-white"
          onClick={handleUpdate}
          disabled={loading}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "130px",
            "&:disabled": {
              backgroundColor: "rgba(13,44,90,0.5)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Updating...
            </Box>
          ) : (
            'Update'
          )}
        </Button>
        <Button
          variant="outlined"
          className="px-4 py-2 fw-medium"
          onClick={() => navigate("/vehicle-management", { state: location.state })}
          sx={{
            borderColor: "rgba(13,44,90,1)",
            color: "rgba(13,44,90,1)",
            "&:hover": {
              borderColor: "rgba(13,44,90,1)",
              backgroundColor: "rgba(13,44,90,0.04)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default EditVehiclePage;


